import { t } from '@lingui/core/macro';
import { debounce } from '@luminovo/commons';
import { XIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { TertiaryIconButton } from '../buttons';
import { FieldConverter } from '../forms/BaseField';
import { InputAdornment } from '../InputAdornment';
import { SearchField } from './SearchField';
interface SearchInputProps<T> {
    value: string | undefined;
    onChange: (value: string) => void;
    autoFocus?: boolean;
    disabled?: boolean;
    placeholder?: string;
    style?: React.CSSProperties;
    size?: 'small' | 'large';
    onKeyEnter?: () => void;
    onClear?: () => void;
    debounceWait?: number;
    formatter?: {
        converter: FieldConverter<T>;
        validation: (value: T) => string | undefined;
    };
    slotProps?: React.ComponentProps<typeof SearchField>['slotProps'];
}

export function SearchInput<T = string>({
    value = '',
    onChange,
    autoFocus,
    style,
    placeholder = t`Type to search`,
    disabled,
    size = 'small',
    onKeyEnter,
    onClear,
    slotProps,
    debounceWait,
    formatter,
}: SearchInputProps<T>): JSX.Element {
    const [localValue, setLocalValue] = React.useState<string>(value);
    const [error, setError] = React.useState<string | undefined>(undefined);
    React.useEffect(() => {
        // oxlint-disable-next-line react/set-state-in-effect -- SOU-4697: the extra render pass is kept deliberately; deriving this during render or resetting via `key` would change what the user sees
        setLocalValue(value);
    }, [value]);

    const onChangeRef = React.useRef(onChange);
    onChangeRef.current = onChange;
    // Reads `onChange` through the ref, so a new `onChange` doesn't recreate the debounce and leave its timer running.
    const debouncedOnChange = React.useMemo(() => {
        return debounce((value: string) => onChangeRef.current(value), debounceWait);
    }, [debounceWait, onChangeRef]);

    const handleLocalChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setLocalValue(newValue);

            if (!formatter) {
                debouncedOnChange(newValue);
                return;
            } else {
                const parsed = formatter.converter.parseValue(newValue);
                if (!parsed.ok) {
                    setError(parsed.message ?? t`Invalid value`);
                    return;
                }
                const error = formatter.validation(parsed.value);
                setError(error);
                if (!error) {
                    debouncedOnChange(parsed.value as string);
                }
            }
        },
        [setLocalValue, debouncedOnChange, setError, formatter],
    );

    const handleKeyUp = React.useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                onKeyEnter?.();
            }
        },
        [onKeyEnter],
    );

    const handleClear = React.useCallback(() => {
        onChange('');
        onClear?.();
    }, [onChange, onClear]);

    return (
        <SearchField
            autoFocus={autoFocus}
            type="text"
            value={localValue}
            onChange={handleLocalChange}
            placeholder={placeholder}
            disabled={disabled}
            size={size}
            onKeyUp={handleKeyUp}
            slotProps={{
                ...slotProps,
                input: {
                    endAdornment: localValue && (
                        <InputAdornment position="end">
                            <TertiaryIconButton
                                size="medium"
                                onClick={handleClear}
                                aria-label={t`Clear search`}
                                style={{ padding: 0, width: 'unset' }}
                            >
                                <XIcon />
                            </TertiaryIconButton>
                        </InputAdornment>
                    ),
                    error: Boolean(error),
                    ...slotProps?.input,
                },
            }}
            style={{ width: '300px', ...style }}
        />
    );
}
